// Runtime SEO helpers - keep the document title, meta description, OG/Twitter
// tags, and canonical URL in sync with the current hash route.
//
// Crawlers do NOT see this (they get the static pre-rendered HTML produced by
// `scripts/generate-static-landings.mjs`). This is purely so a human user who
// opens `/#/connections` and shares the URL with a chat client that scrapes
// runtime DOM (some do, eg Telegram's IM bot, Slack's unfurl proxy if it
// renders) gets a usable preview.

interface RouteSeo {
  title: string;
  description: string;
  ogImage?: string;
  /** Canonical URL path (no scheme/host). Defaults to `/`. */
  canonicalPath: string;
}

const SITE_URL = 'https://nostr.gives';
const DEFAULT_OG = `${SITE_URL}/og/default.png`;

interface GameMeta {
  id: string;
  name: string;
  tagline: string;
}

// Static map - matches the canonical GAME_IDS order. The values here are
// duplicated in `scripts/generate-static-landings.mjs` and `sitemap.xml`.
// Source of truth for the catalogue itself lives in `registry.gen.ts`.
const GAME_META: Record<string, GameMeta> = {
  connections: { id: 'connections', name: 'Connections', tagline: 'Sort 16 words into 4 groups' },
  worldle: { id: 'worldle', name: 'Worldle', tagline: 'Guess the country from its silhouette' },
  flagle: { id: 'flagle', name: 'Flagle', tagline: 'Guess the country from its flag' },
  nerdle: { id: 'nerdle', name: 'Equate', tagline: 'Solve the daily equation in six tries' },
  'spelling-bee': { id: 'spelling-bee', name: 'Spelling Bee', tagline: 'Make as many words as you can from 7 letters' },
  'chess-puzzle': { id: 'chess-puzzle', name: 'Chess Puzzle', tagline: 'Find the winning move in a daily position' },
  heardle: { id: 'heardle', name: 'Heardle', tagline: 'Name the track from a short audio clip' },
  minesweeper: { id: 'minesweeper', name: 'Minesweeper', tagline: 'Clear every safe cell without tripping a mine' },
  connect4: { id: 'connect4', name: 'Connect4', tagline: 'Drop four in a row against the daily CPU' },
  nonogram: { id: 'nonogram', name: 'Nonogram', tagline: 'Reveal the hidden picture from the row and column clues' },
  longword: { id: 'longword', name: 'LongWord', tagline: 'Daily long-word puzzle. 6-9 letters. First letter shown.' },
  mathdoku: { id: 'mathdoku', name: 'MathDoku', tagline: 'Daily arithmetic grid' },
  compound: { id: 'compound', name: 'Compound', tagline: 'Daily compound-word puzzle. Two clue words, find the missing link.' },
  slitherlink: { id: 'slitherlink', name: 'Slitherlink', tagline: 'Draw the one true loop. Hit every clue.' },
  battleships: { id: 'battleships', name: 'Battleships', tagline: 'Outwit the CPU. Sink before sunk.' },
  '2048': { id: '2048', name: '2048', tagline: 'Daily 2048. Reach the 2048 tile in the fewest moves. Same spawn sequence for everyone.' },
  echo: { id: 'echo', name: 'Echo', tagline: 'Daily Echo. Watch the sequence, tap it back. Fifteen rounds, one mistake ends the game.' },
  acrostic: { id: 'acrostic', name: 'Acrostic', tagline: 'Daily Acrostic. Solve clues, watch letters propagate across the quote. Find the hidden author.' },
  'letter-boxed': { id: 'letter-boxed', name: 'Letter Boxed', tagline: 'Daily Letter Boxed. Twelve letters around a square. Chain words to use them all in the fewest possible.' },
  leword: { id: 'leword', name: 'LeWord', tagline: 'Guess the 5-letter word in 6 tries. Free daily word puzzle. Hard Mode supported.' },
  'cross-number': { id: 'cross-number', name: 'Cross Number', tagline: 'Daily Cross Number. Solve a numeric crossword - every clue is an arithmetic expression.' },
  foldle: { id: 'foldle', name: 'Foldle', tagline: 'Daily Foldle. Four rounds of mental folding. Pick the 3D solid that the unfolded net wraps into.' },
  akari: { id: 'akari', name: 'Akari', tagline: 'Daily Akari. Light every white cell. No two bulbs in line. Walls show their bulb count.' },
  kakuro: { id: 'kakuro', name: 'Kakuro', tagline: 'Daily Kakuro. Fill every white cell so each row-run and column-run sums to its clue. No repeats within a run.' },
};

function routeSeoForHash(hash: string): RouteSeo {
  const path = (hash || '#/').slice(1) || '/';

  // Home
  if (path === '/' || path === '') {
    return {
      title: 'nostr.gives - daily puzzles for Nostr',
      description: 'Daily mini-games for Nostr. Connections, Worldle, Spelling Bee, Chess Puzzle, Heardle and more. Five minutes, then back to your day.',
      canonicalPath: '/',
    };
  }

  // Leaderboard
  if (path === '/leaderboard' || path.startsWith('/leaderboard?') || path.startsWith('/leaderboard&')) {
    return {
      title: 'Leaderboard - nostr.gives',
      description: 'Today\'s top scores across every nostr.gives daily puzzle.',
      canonicalPath: '/leaderboard',
    };
  }

  // Help pages
  if (path === '/help/auth') {
    return { title: 'Sign in - nostr.gives', description: 'How sign-in works on nostr.gives (NIP-46 bunker and NIP-07 browser extension).', canonicalPath: '/help/auth' };
  }
  if (path === '/help/privacy') {
    return { title: 'Privacy - nostr.gives', description: 'Privacy and data handling on nostr.gives.', canonicalPath: '/help/privacy' };
  }
  if (path === '/help/terms') {
    return { title: 'Terms - nostr.gives', description: 'Terms of use for nostr.gives.', canonicalPath: '/help/terms' };
  }

  // Game route: /<gameId>
  if (path.startsWith('/')) {
    const id = path.slice(1).split(/[?&]/)[0] ?? '';
    const meta = GAME_META[id];
    if (meta) {
      return {
        title: `${meta.name} - daily puzzle - nostr.gives`,
        description: `${meta.name}. ${meta.tagline}. Same puzzle for everyone worldwide. Resets 00:00 UTC.`,
        ogImage: `${SITE_URL}/og/${meta.id}.png`,
        canonicalPath: `/${meta.id}`,
      };
    }
  }

  // Unknown route - keep the home defaults so we never set a misleading title.
  return {
    title: 'nostr.gives - daily puzzles for Nostr',
    description: 'Daily mini-games for Nostr. Five minutes, then back to your day.',
    canonicalPath: '/',
  };
}

function setMeta(selector: string, attr: 'content' | 'href', value: string): void {
  const el = document.head.querySelector(selector) as (HTMLElement | null);
  if (!el) return;
  el.setAttribute(attr, value);
}

function ensureMeta(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector(selector) as (HTMLElement | null);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Sync `<title>`, `<meta description>`, OG/Twitter tags, and the canonical
 * `<link>` to the current hash route. Call on initial paint and every
 * `hashchange`.
 */
export function applyRouteSeo(): void {
  const seo = routeSeoForHash(window.location.hash);
  const og = seo.ogImage ?? DEFAULT_OG;
  const canonical = `${SITE_URL}${seo.canonicalPath}`;

  document.title = seo.title;
  setMeta('meta[name="description"]', 'content', seo.description);
  setMeta('meta[property="og:title"]', 'content', seo.title);
  setMeta('meta[property="og:description"]', 'content', seo.description);
  setMeta('meta[property="og:url"]', 'content', canonical);
  setMeta('meta[property="og:image"]', 'content', og);
  setMeta('meta[name="twitter:title"]', 'content', seo.title);
  setMeta('meta[name="twitter:description"]', 'content', seo.description);
  setMeta('meta[name="twitter:image"]', 'content', og);

  const link = ensureMeta('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  });
  link.setAttribute('href', canonical);
}
